import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  Unsubscribe,
  Timestamp,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { ApiResponse, UserAPI, AdminAPI } from '@shared/types/api';

export type AdminConversationKind = 'table' | 'group' | 'kiki';

// Types for admin chat data
export interface AdminConversation {
  id: string;
  participants: string[];
  participantDetails: Record<string, { name: string }>;
  lastMessage: {
    text: string;
    senderId: string;
    type: 'text' | 'system';
    createdAt: string;
  } | null;
  updatedAt: string;
  unreadCounts: Record<string, number>;
  title?: string;
  subtitle?: string;
  tableId?: string;
  eventTimeId?: string;
  starred?: boolean;
  starredAt?: string;
  kind?: AdminConversationKind;
  chatInfo?: UserAPI.ChatInfoData;
}

export interface AdminMessage {
  id: string;
  content: string;
  senderId: string;
  type: 'text' | 'system';
  createdAt: string;
}

/**
 * Hook to listen to all conversations in real-time (admin view)
 */
export function useAdminConversationsListener(enabled: boolean) {
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const db = getFirestoreDb();
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        orderBy('updatedAt', 'desc'),
        firestoreLimit(100)
      );

      unsubscribeRef.current = onSnapshot(
        q,
        (snapshot) => {
          const convos: AdminConversation[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            convos.push({
              id: doc.id,
              participants: data.participants || [],
              participantDetails: data.participantDetails || {},
              lastMessage: data.lastMessage
                ? {
                    text: data.lastMessage.text,
                    senderId: data.lastMessage.senderId,
                    type: data.lastMessage.type,
                    createdAt: data.lastMessage.createdAt instanceof Timestamp
                      ? data.lastMessage.createdAt.toDate().toISOString()
                      : new Date().toISOString(),
                  }
                : null,
              updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate().toISOString()
                : new Date().toISOString(),
              unreadCounts: data.unreadCounts || {},
              title: data.title,
              subtitle: data.subtitle,
              tableId: data.tableId,
              eventTimeId: data.eventTimeId,
              starred: data.starred === true,
              starredAt: data.starredAt instanceof Timestamp
                ? data.starredAt.toDate().toISOString()
                : undefined,
              kind: data.kind,
              chatInfo: data.chatInfo,
            });
          });
          setConversations(convos);
          setIsLoading(false);
        },
        (err) => {
          console.error('Error listening to conversations:', err);
          setError(err);
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.error('Error setting up conversations listener:', err);
      setError(err as Error);
      setIsLoading(false);
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [enabled]);

  return { conversations, isLoading, error };
}

/**
 * Hook to listen to messages for a specific conversation in real-time
 */
export function useAdminMessagesListener(conversationId: string | null) {
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  useEffect(() => {
    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!conversationId) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const db = getFirestoreDb();
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      const q = query(
        messagesRef,
        orderBy('createdAt', 'asc'),
        firestoreLimit(200)
      );

      unsubscribeRef.current = onSnapshot(
        q,
        (snapshot) => {
          const msgs: AdminMessage[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            msgs.push({
              id: doc.id,
              content: data.content,
              senderId: data.senderId,
              type: data.type || 'text',
              createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate().toISOString()
                : new Date().toISOString(),
            });
          });
          setMessages(msgs);
          setIsLoading(false);
        },
        (err) => {
          console.error('Error listening to messages:', err);
          setError(err);
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.error('Error setting up messages listener:', err);
      setError(err as Error);
      setIsLoading(false);
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [conversationId]);

  return { messages, isLoading, error };
}


export type ChatUsersResponse = ApiResponse<{
  users: Record<string, { id: string; name: string; hasDevice?: boolean; devicePlatforms?: string[] }>;
}>;

/**
 * Hook to fetch user details for participants
 * Returns a map of userId -> user details
 */
export function useAdminChatUserDetails(participantIds: string[]) {
  const idsKey = participantIds.sort().join(',');
  
  return useQuery({
    queryKey: ['admin', 'chats', 'users', idsKey],
    queryFn: async (): Promise<ChatUsersResponse> => {
      if (participantIds.length === 0) {
        return {success: true, data: {users: {}}};
      }
      
      return await api.post('/chat/users', { userIds: participantIds });
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: participantIds.length > 0,
  });
}

interface SendSystemMessageResponse {
  success: boolean;
  data?: { messageId: string };
  error?: string;
}

/**
 * Mutation hook for admins to send system messages to a conversation
 */
export function useSendAdminSystemMessage() {
  return useMutation({
    mutationFn: async ({ conversationId, content }: { conversationId: string; content: string }): Promise<SendSystemMessageResponse> => {
      return await api.post(`/admin/chats/${conversationId}/system-message`, { content });
    },
  });
}

/**
 * Mutation hook for admins to star or unstar a conversation
 */
export function useToggleConversationStar() {
  return useMutation({
    mutationFn: async ({ conversationId, starred }: { conversationId: string; starred: boolean }): Promise<{ success: boolean; error?: string }> => {
      return await api.post(`/admin/chats/${conversationId}/star`, { starred });
    },
  });
}

interface CreateGroupChatResponse {
  success: boolean;
  data?: { conversationId: string };
  error?: string;
}

/**
 * Create a new group chat with a name and optional initial participants.
 * Group chats are not tied to a table — used for arbitrary rooms (e.g. neighborhood chats).
 */
export function useCreateGroupChat() {
  return useMutation({
    mutationFn: async ({ name, userIds }: { name: string; userIds: string[] }): Promise<CreateGroupChatResponse> => {
      return await api.post('/admin/chats/group', { name, userIds });
    },
  });
}

/**
 * Create a "close out" group chat for everyone assigned to a table in the
 * given neighborhood on the given event. The server resolves participants,
 * sets the venue (bar) and a templated description.
 */
export function useCreateCloseOutChat() {
  return useMutation({
    mutationFn: async (
      input: AdminAPI.CreateCloseOutChatRequest
    ): Promise<AdminAPI.CreateCloseOutChatResponse> => {
      return await api.post('/admin/chats/close-out', input);
    },
  });
}

/**
 * Re-send the "Close out chat opened" broadcast (broadcast 65) to all
 * participants of an existing close-out chat. The chat must already have
 * `chatInfo` set (venue + meeting time).
 */
export function useResendCloseOutChatBroadcast() {
  return useMutation({
    mutationFn: async (
      conversationId: string
    ): Promise<AdminAPI.ResendCloseOutChatBroadcastResponse> => {
      return await api.post(
        `/admin/chats/${conversationId}/close-out/resend-broadcast`,
        {}
      );
    },
  });
}

interface UpdateParticipantsResponse {
  success: boolean;
  data?: { participants: string[] };
  error?: string;
}

/**
 * Add users to an existing conversation.
 */
export function useAddChatParticipants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, userIds }: { conversationId: string; userIds: string[] }): Promise<UpdateParticipantsResponse> => {
      return await api.post(`/admin/chats/${conversationId}/participants`, { userIds });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'chats', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'chats', variables.conversationId] });
    },
  });
}

/**
 * Update editable conversation metadata: title and/or the generic ChatInfo
 * (venue + meeting time + description). Omit a field to leave it unchanged;
 * pass `chatInfo: null` to clear it.
 */
export function useUpdateChatInfo() {
  return useMutation({
    mutationFn: async ({
      conversationId,
      chatInfo,
      title,
    }: {
      conversationId: string;
      chatInfo?: UserAPI.ChatInfoData | null;
      title?: string;
    }): Promise<{ success: boolean; error?: string }> => {
      return await api.put(`/admin/chats/${conversationId}/info`, { chatInfo, title });
    },
  });
}

/**
 * Delete a conversation entirely, including all messages.
 */
export function useDeleteChat() {
  return useMutation({
    mutationFn: async ({ conversationId }: { conversationId: string }): Promise<{ success: boolean; error?: string }> => {
      return await api.delete(`/admin/chats/${conversationId}`);
    },
  });
}

/**
 * Remove users from an existing conversation.
 */
export function useDeleteAllGroupChats() {
  return useMutation({
    mutationFn: async (): Promise<{ success: boolean; data?: { deletedCount: number }; error?: string }> => {
      return await api.delete('/admin/chats/group/all');
    },
  });
}

export function useRemoveChatParticipants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, userIds }: { conversationId: string; userIds: string[] }): Promise<UpdateParticipantsResponse> => {
      return await api.delete(`/admin/chats/${conversationId}/participants`, { userIds });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'chats', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'chats', variables.conversationId] });
    },
  });
}
